import {
  icon,
  IconDefinition,
  library,
} from '@fortawesome/fontawesome-svg-core';
import * as duo from '@fortawesome/pro-duotone-svg-icons';
import * as regular from '@fortawesome/pro-regular-svg-icons';
import * as solid from '@fortawesome/pro-solid-svg-icons';
import { token } from '@thiememeulenhoff/lit-sdk';
import { IconName } from '@thiememeulenhoff/types';
import { CSSResult } from 'lit';
import { custom } from './custom/custom.icons';

/**
 * Mapping of icon names to their respective FontAwesome icon definitions.
 */
export const icons: Record<IconName, IconDefinition> = {
  adaptive: solid.faSplit,
  arrange: solid.faSendBackward,
  'arrow-down': regular.faArrowDown,
  'arrow-left': regular.faArrowLeft,
  'arrow-dash-right': custom.fakArrowDashRight,
  'arrow-right': regular.faArrowRight,
  'arrow-up': regular.faArrowUp,
  'arrow-up-right-from-square': solid.faArrowUpRightFromSquare,
  'angles-up-down': duo.faAnglesUpDown,
  'angles-up-down-solid': solid.faAnglesUpDown,
  award: solid.faAward,
  ban: solid.faBan,
  book: solid.faBook,
  'book-regular': solid.faBook,
  'book-alt': custom.fakBookAlt,
  bookmark: regular.faBookmark,
  'bookmark-solid': solid.faBookmark,
  'book-bookmark': solid.faBookBookmark,
  'book-open': regular.faBookOpen,
  'book-open-magnifying-glass': custom.fakSolidBookOpenMagnifyingGlass,
  books: solid.faBooks,
  bullseye: solid.faBullseye,
  'caret-right': solid.faCaretRight,
  'caret-down': solid.faCaretDown,
  'caret-up': solid.faCaretUp,
  check: regular.faCheck,
  'check-multi': solid.faCheckDouble,
  'check-solid': solid.faCheck,
  'chevron-down': solid.faChevronDown,
  'chevron-left': solid.faChevronLeft,
  'chevrons-left': solid.faChevronsLeft,
  'chevron-right': solid.faChevronRight,
  'chevrons-right': solid.faChevronsRight,
  'chevron-up': solid.faChevronUp,
  circle: solid.faCircle,
  'circle-caret-down': regular.faCircleCaretDown,
  'circle-caret-down-solid': solid.faCircleCaretDown,
  'circle-caret-up': regular.faCircleCaretUp,
  'circle-caret-up-solid': solid.faCircleCaretUp,
  'circle-check-solid': solid.faCircleCheck,
  'circle-chevron-down': solid.faCircleChevronDown,
  'circle-chevron-up': solid.faCircleChevronUp,
  'circle-exclamation': regular.faCircleExclamation,
  'circle-location-arrow-solid': solid.faCircleLocationArrow,
  'circle-small': solid.faCircleSmall,
  'circle-info-solid': solid.faCircleInfo,
  'clipboard-question': regular.faClipboardQuestion,
  clock: solid.faClock,
  close: regular.faXmark,
  copy: regular.faCopy,
  copyright: regular.faCopyright,
  colofon: solid.faMemo,
  dash: solid.faDash,
  delete: regular.faTrash,
  'delete-solid': solid.faTrash,
  digibook: custom.fakDigibook,
  download: solid.faDownToBracket,
  doc: solid.faFileDoc,
  droplet: solid.faDroplet,
  dumbell: solid.faDumbbell,
  edit: regular.faPen,
  ellipsis: regular.faEllipsis,
  'ellipsis-vertical': regular.faEllipsisVertical,
  danger: regular.faCircleExclamation,
  'danger-solid': solid.faCircleExclamation,
  ear: regular.faEar,
  enter: solid.faTurnDownLeft,
  eraser: custom.fakEraser,
  'eraser-transparent': custom.fakEraserTransparent,
  error: regular.faCircleXmark,
  'error-solid': solid.faCircleXmark,
  eye: regular.faEye,
  'eye-slash': regular.faEyeSlash,
  file: solid.faFile,
  'file-certificate': solid.faFileCertificate,
  'file-download': regular.faFileArrowDown,
  'file-download-solid': solid.faFileArrowDown,
  'file-open': regular.faFile,
  'file-lines': solid.faFileLines,
  'file-lines-open': regular.faFileLines,
  'file-lines-pen': custom.fakSolidFileLinesPen,
  'file-pen': solid.faFilePen,
  filter: solid.faFilter,
  'external-link': regular.faArrowUpRightFromSquare,
  'external-link-line': regular.faArrowRightLongToLine,
  'fullscreen-enter': regular.faExpand,
  'fullscreen-exit': regular.faCompress,
  gauge: solid.faGauge,
  globe: regular.faEarthEurope,
  'globe-grid': solid.faGlobe,
  'globe-grid-regular': regular.faGlobe,
  grip: solid.faGripDots,
  'grip-vertical': solid.faGripDotsVertical,
  group: solid.faUsers,
  hide: solid.faEyeSlash,
  info: regular.faCircleInfo,
  'info-solid': solid.faCircleInfo,
  licenses: solid.faMemo,
  list: regular.faList,
  'list-check': regular.faListCheck,
  loading: regular.faLoader,
  lock: solid.faLockKeyhole,
  'lock-open': solid.faLockKeyholeOpen,
  logout: solid.faRightFromBracket,
  highlighter: solid.faHighlighter,
  home: custom.fakHome,
  manual: solid.faMemoCircleInfo,
  math: solid.faOmega,
  medal: custom.fakMedal,
  menu: solid.faBars,
  message: solid.faMessage,
  'message-lines': solid.faMessageLines,
  messages: solid.faMessages,
  microphone: solid.faMicrophone,
  'new-stars': custom.fakNewStars,
  newspaper: regular.faNewspaper,
  notes: custom.fakNotes,
  notification: solid.faBell,
  open: solid.faShareFromSquare,
  'open-book': custom.fakOpenBook,
  'paper-plane': solid.faPaperPlane,
  'paper-plane-empty': regular.faPaperPlane,
  pause: solid.faPause,
  pdf: solid.faFilePdf,
  pencil: solid.faPen,
  'pencil-square': solid.faPenToSquare,
  'pen-field': solid.faPenField,
  'pen-line': custom.fakPenLine,
  play: solid.faPlay,
  plus: solid.faPlus,
  ppt: solid.faFilePpt,
  print: solid.faPrint,
  receipt: solid.faReceipt,
  'recovery-convert': custom.fakRecoveryConvert,
  redo: regular.faRedo,
  refresh: regular.faRefresh,
  resize: solid.faLeftRight,
  retry: regular.faRedo,
  results: solid.faChartMixed,
  save: solid.faFloppyDisk,
  search: regular.faMagnifyingGlass,
  send: solid.faPaperPlaneTop,
  settings: solid.faGear,
  show: solid.faEye,
  shuffle: solid.faShuffle,
  sliders: solid.faSliders,
  sparkles: solid.faSparkles,
  star: solid.faStar,
  'star-empty': regular.faStar,
  'star-half': solid.faStarHalfStroke,
  'self-test': solid.faFlag,
  student: solid.faUserGraduate,
  submit: solid.faPaperPlane,
  'submit-empty': regular.faPaperPlane,
  success: regular.faCircleCheck,
  'success-solid': solid.faCircleCheck,
  theory: regular.faFileInvoice,
  toggle: custom.fakToggle,
  'trend-up': custom.fakTrendUp,
  'trend-up-line': solid.faArrowTrendUp,
  undo: regular.faUndo,
  up: regular.faUp,
  'up-solid': solid.faUp,
  'uneven-grid': custom.fakUnevenGrid,
  upload: solid.faArrowUpFromBracket,
  user: solid.faUser,
  'user-teacher': solid.faUserTie,
  'user-ninja': solid.faUserNinja,
  'user-chalkboard': solid.faChalkboardUser,
  voiceover: solid.faVolumeHigh,
  warning: solid.faTriangleExclamation,
  'warning-duo': duo.faTriangleExclamation as IconDefinition,
  'wave-triangle': solid.faWaveTriangle,
  xls: solid.faFileXls,
  'zoom-in': regular.faMagnifyingGlassPlus,
  'zoom-in-solid': solid.faMagnifyingGlassPlus,
  'zoom-out': regular.faMagnifyingGlassMinus,
} as const;

export const duoIconColors: { [key: string]: CSSResult } = {
  'warning-duo': token('foreground-primary'),
} as const;

export const getIcon = (name: IconName) => {
  const definition = icons[name];
  return definition ? icon(icons[name]).html[0] : undefined;
};

export const initializeIconLibrary = () => {
  const iconLibrary = library;
  Object.values(icons).forEach((icon) => {
    iconLibrary.add(icon);
  });
  return library;
};
